import { isToday, isTomorrow, isYesterday } from 'date-fns';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import i18n from 'i18next';
import { match } from 'ts-pattern';
import { parseDate, getTimeZoneOffsetValue } from '../../datetime';
import {
  createDateFormatConfig,
  createDateTimeFormat,
  createTimeFormatConfig,
  getShortFullDateTimeFormat,
} from './formats';
import { DateFormat, TimeFormat } from './types';

export type LocalizedDatetimeOptions = {
  dateStyle?: DateFormat;
  locale?: string;
  relative?: boolean;
  showOffset?: boolean;
  showTime?: boolean;
  timeStyle?: TimeFormat;
  timeZone?: string;
  i18nKey?: string;
};

const convertMsToHourMinutes = (milliseconds: number): string =>
  pipe(
    milliseconds,
    (ms) => ms / (60 * 1000),
    (m) => [Math.floor(m / 60), m % 60],
    array.map((n) => n.toString()),
    array.map((s) => s.padStart(2, '0')),
    (as) => as.join(':'),
  );

// Helper function to generate a formatted datetime string and pluck out the TZ offset
const generateTzOffset = (date: Date, timeZone: string) => {
  const offset = getTimeZoneOffsetValue(timeZone, date);

  if (offset === 0) {
    return 'UTC';
  }

  const isNegative = offset < 0;
  const duration = convertMsToHourMinutes(Math.abs(offset));

  return `UTC${isNegative ? '-' : '+'}${duration}`;
};

const generateDate = (date: Date, style?: DateFormat, relative?: boolean, locale?: string) => {
  const isRelativeAnd = (f: (date: Date) => boolean) => (d: Date) => Boolean(relative) && f(d);
  const translate = (key: string) => () => i18n.t(`common:datetime.relative.${key}`);

  return match(date)
    .when(isRelativeAnd(isYesterday), translate('yesterday'))
    .when(isRelativeAnd(isToday), translate('today'))
    .when(isRelativeAnd(isTomorrow), translate('tomorrow'))
    .otherwise(createDateTimeFormat(locale, createDateFormatConfig(style)).format);
};

// Formats date based on options
export const localizeDateTime = (
  date: Date = parseDate(undefined, true),
  options: LocalizedDatetimeOptions = {},
) => {
  const datePart = generateDate(date, options.dateStyle, options.relative, options.locale);

  if (!options.showTime) {
    return datePart;
  }

  const timePart = createDateTimeFormat(
    options.locale,
    createTimeFormatConfig(options.timeStyle, options.timeZone),
  ).format(date);

  const offsetPart =
    options.showOffset && options.timeZone ? generateTzOffset(date, options.timeZone) : undefined;

  const translationKey =
    (options.i18nKey ?? options.showOffset)
      ? 'common:datetime.withOffset'
      : 'common:datetime.withoutOffset';

  return i18n.t(translationKey, { date: datePart, time: timePart, offset: offsetPart });
};

/**
 * Returns a localized date & time
 * @deprecated use localizeDateTime
 */
export const localizeCompactDateTime = (date: Date = new Date(), locale?: string) =>
  getShortFullDateTimeFormat(locale).format(date);
